import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import moment, { Moment } from 'moment-timezone';
import { Col, Dropdown, Row, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import * as _ from 'lodash';

import { ReactComponent as CalendarIcon } from 'app/assets/img/calendar-icon.svg';
import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow-down-icon.svg';

import StyledDatePicker from '../../components/StyledDatePicker';
import { formatDate } from '..';
import StyledSelect from '../../components/StyledSelect';
import { selectListPark } from 'app/pages/Home/slice/selectors';
import { GET_PARKS } from 'app/pages/Home/slice';
import { convertVItoEN } from '../../CheckTickets/helper';
import StyledSearchInput from '../../components/StyledSearchInput';
import InfinitySelect from 'app/components/SelectMulti/InfinitySelect';
import {
  exportHistoryExcelItemListApi,
  getDataAgentApi,
  getDataSelectTicketApi,
} from 'app/services/checkTicketApi';
import { Permission } from 'app/hooks/usePermission';
import RangeDatePickerCustom from '../../CheckTickets/RangeDatePickerCustom';
import styled from 'styled-components';
import StyledButton from 'app/components/StyledButton';
import StyledTable from 'app/components/StyledTable';
import useTitleSize from 'app/components/StyledTable/useTitleSize';
import { useHistory } from 'react-router-dom';
import useGetDetailTicket from '../../hooks/useGetDetailTicket';
import { StyledPopover } from '../../CheckTickets/styled';
import { RepresentativeList } from '../../layouts/ListTicketLayout';
import { formatNumber } from 'utils/helper';
import TooltipParagraph from 'app/pages/Catalog/tooltipParagraph';
import { ReactComponent as ExportExcel } from 'app/assets/img/export-excel-icon.svg';
import useWindowSize from 'app/hooks/useWindowSize';
import StyledPagination from 'app/components/StyledPagination';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { StyledStack } from 'app/components/Stack';
import { ReactComponent as FilterIcon } from 'app/assets/img/filter-icon.svg';
import useFetchData from 'app/hooks/useFetchData';
import { getListItemApi } from 'app/services/authApi';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import StyledTabs from 'app/components/StyledTabs';

export type FilterFormValues = {
  usageFrom: Moment | null;
  usageTo: Moment | null;
  parkId: string;
  orderId: string;
  ticketShowIds: string[];
  agentId: string;
  typeTickets: string[];
  orderDateFrom: Moment | null;
  orderDateTo: Moment | null;
  page: number;
  limit: number;
};

const transfromFilterValues = (rawData: FilterFormValues) => {
  return {
    usageFrom: rawData.usageFrom?.format('YYYY-MM-DD') ?? '',
    usageTo: rawData.usageTo?.format('YYYY-MM-DD') ?? '',
    parkId: rawData.parkId,
    orderId: !!rawData.orderId ? rawData.orderId : undefined,
    ticketShowIds: rawData.ticketShowIds.join(),
    agentId: rawData.agentId,
    typeTickets: rawData.typeTickets.join(),
    orderDateFrom: rawData.orderDateFrom?.format('YYYY-MM-DD') ?? '',
    orderDateTo:
      rawData.orderDateTo?.format('YYYY-MM-DD') ??
      rawData.orderDateFrom?.format('YYYY-MM-DD') ??
      '',
    page: rawData.page,
    limit: rawData.limit,
  };
};

const Filter: FC<{ onSearch: (data: FilterFormValues) => void }> = ({
  onSearch,
}) => {
  const translations = useSelector(selectTranslations);
  const dispatch = useDispatch();
  const [dataAgent, setDataAgent] = useState([]);
  const { control, setValue, handleSubmit, getValues } =
    useFormContext<FilterFormValues>();
  const { device } = useWindowSize();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const isDesktop = device === 'desktop';

  const infoUser = useSelector(selectInfoUser);
  const isSaleMng =
    infoUser?.role_code === Permission.ROLE_SALE_MANAGER ||
    infoUser?.role_code === Permission.ROLE_SALE_SHG;

  const listPark = useSelector(selectListPark);

  const travelLocation = useMemo(() => {
    return [
      ...[{ value: '', label: translations?.['b2b.text.all'] }],
      ...listPark?.map(item => ({
        value: `${item.id}`,
        label: item.name,
      })),
    ];
  }, [listPark, translations]);

  const ticketTypeOptions = useMemo(
    () => [
      { label: translations?.['b2b.txt.ticket-normal'] ?? '', value: 'NORMAL' },
      {
        label: translations?.['b2b.txt.ticket-normal-gift'] ?? '',
        value: 'NORMAL_GIFT',
      },
      { label: translations?.['b2b.txt.ticket-foc'] ?? '', value: 'FOC' },
    ],
    [translations],
  );

  const filterComponent = (
    <Row gutter={isDesktop ? [16, 16] : [8, 8]}>
      <Col span={isDesktop ? 6 : 24}>
        <Controller
          control={control}
          name="usageFrom"
          render={({ field }) => (
            <StyledDatePicker
              suffixIcon={<CalendarIcon />}
              placeholder={translations?.['b2b.txt.usageDateFrom'] ?? ''}
              format={date => formatDate(date, translations)}
              allowClear
              style={{ width: '100%' }}
              minWidthInput="initial"
              disabledDate={current =>
                current && current < moment('2025-08-01').startOf('day')
              }
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 6 : 24}>
        <Controller
          control={control}
          name="usageTo"
          render={({ field }) => (
            <StyledDatePicker
              suffixIcon={<CalendarIcon />}
              placeholder={translations?.['b2b.txt.usageDateTo'] ?? ''}
              format={date => formatDate(date, translations)}
              allowClear
              style={{ width: '100%' }}
              minWidthInput="initial"
              disabledDate={current =>
                current && current < moment('2025-08-01').startOf('day')
              }
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 6 : 24}>
        <Controller
          control={control}
          name="parkId"
          render={({ field }) => (
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              suffixIcon={<ArrowDownIcon />}
              placeholder={translations ? translations['b2c.txt.park'] : ''}
              options={travelLocation}
              allowClear
              style={{ width: '100%' }}
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 6 : 24}>
        <Controller
          control={control}
          name="orderId"
          render={({ field }) => (
            <StyledSearchInput
              placeholder={
                translations ? translations['b2c.txt.orderNumber'] : ''
              }
              width="100%"
              {...field}
              onBlur={() => {
                setValue('orderId', getValues('orderId').trim());
              }}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 5 : 24}>
        <Controller
          control={control}
          name="ticketShowIds"
          render={({ field }) => (
            <InfinitySelect
              placeholder={translations?.['b2b.booking.products']}
              getDataApi={getDataSelectTicketApi}
              height="44px"
              style={{ width: '100%' }}
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 5 : 24}>
        <Controller
          control={control}
          name="agentId"
          render={({ field }) => (
            <StyledSelect
              filterOption={(input, option) =>
                (convertVItoEN(option?.label) ?? '').includes(
                  convertVItoEN(input),
                )
              }
              showSearch
              isFocus
              suffixIcon={<ArrowDownIcon />}
              placeholder={translations ? translations['b2b.txt.orderer'] : ''}
              options={dataAgent}
              allowClear
              style={{ width: '100%' }}
              {...field}
            />
          )}
        />
      </Col>
      <Col span={isDesktop ? 4 : 24}>
        <Controller
          control={control}
          name="typeTickets"
          render={({ field }) => (
            <StyledSelect
              suffixIcon={<ArrowDownIcon />}
              placeholder={
                translations ? translations['b2b.txt.ticket-type'] : ''
              }
              options={ticketTypeOptions}
              style={{
                width: '100%',
              }}
              allowClear
              mode="tags"
              maxTagCount={1}
              {...field}
            />
          )}
        />
      </Col>
      <RangeDatePickerContainer span={isDesktop ? 6 : 24}>
        <RangeDatePickerCustom
          placeholder={translations ? translations['b2b.txt.date-order'] : ''}
          minDate={null}
          isClearable
          onChange={([start, end]) => {
            setValue('orderDateFrom', start ? moment(start) : null);
            setValue('orderDateTo', end ? moment(end) : null);
          }}
        />
      </RangeDatePickerContainer>
      <Col span={isDesktop ? 4 : 24}>
        <StyledButton
          onClick={handleSubmit(onSearch)}
          style={{
            background: '#FFFFFF',
            border: '1px solid #F8B117',
            color: '#F8B117',
            width: '100%',
            height: 44,
            flex: 1,
          }}
        >
          {translations ? translations['b2c.txt.search'] : ''}
        </StyledButton>
      </Col>
    </Row>
  );

  useEffect(() => {
    if (!listPark?.length) dispatch(GET_PARKS());
  }, [dispatch, listPark]);

  useEffect(() => {
    const getDataAgent = async () => {
      try {
        const { data } = await getDataAgentApi();
        if (!_.isEmpty(data?.data)) {
          const resAgents = data?.data.map((item: any) => {
            return {
              label: item.name,
              value: item.id,
            };
          });
          const resAgentsEx: any = isSaleMng
            ? [
                {
                  value: '',
                  label: translations?.['b2b.text.all'],
                },
                ...resAgents,
              ]
            : resAgents;
          setDataAgent(resAgentsEx);
        }
      } catch (error) {}
    };

    getDataAgent();
  }, [isSaleMng, translations]);

  return (
    <>
      {isDesktop ? (
        filterComponent
      ) : (
        <Dropdown
          visible={mobileMenuVisible}
          trigger={['click']}
          onVisibleChange={visible => setMobileMenuVisible(visible)}
          getPopupContainer={trigger => trigger.parentNode as HTMLElement}
          overlayStyle={{ zIndex: 3 }}
          overlay={
            <StyledStack
              style={{
                background: 'white',
                width: 292,
                padding: 16,
                borderRadius: 10,
                boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
              }}
            >
              {filterComponent}
            </StyledStack>
          }
        >
          <StyledButton
            bgColor="#fff"
            style={{
              border: '1px solid #EDEDED',
              borderRadius: 10,
              width: 41,
              height: 41,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <FilterIcon width={16} height={16} color="#000" />
          </StyledButton>
        </Dropdown>
      )}
    </>
  );
};

const PurcharseHistoryDetail: FC = () => {
  const translations = useSelector(selectTranslations);
  const history = useHistory();
  const [pageLoading, setPageLoading] = useState(false);
  const { device } = useWindowSize();
  const isDesktop = device === 'desktop';
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();

  const filterForm = useForm<FilterFormValues>({
    defaultValues: {
      usageFrom: undefined,
      usageTo: undefined,
      parkId: undefined,
      orderId: '',
      ticketShowIds: [],
      agentId: undefined,
      typeTickets: [],
      orderDateFrom: undefined,
      orderDateTo: undefined,
      page: 1,
      limit: 20,
    },
  });

  const filterRef = useRef<FilterFormValues>(filterForm.getValues());
  const anchorPointerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  const initFetchListItem = useRef(true);

  const { result: listItemResponse, onFetchData: onFetchListItem } =
    useFetchData({
      actionApi: getListItemApi,
      payloadData: transfromFilterValues(filterForm.getValues()),
      IIFE: false,
      onBefore: () => {
        setPageLoading(true);
      },
      onFinally: () => {
        setPageLoading(false);
      },
    });

  const { onClickShowDetail, isGetingDetail } = useGetDetailTicket({
    screen: '/account',
    setIsLoading: setPageLoading,
  });

  const titleColumnSizes = useTitleSize({
    title: [
      'b2c.txt.date-order',
      'b2c.txt.orderNumber',
      'b2b.txt.root-order',
      'b2b.txt.ticketPark',
      'b2b.txt.validFrom',
      'b2b.txt.validTo',
      'b2b.txt.ticketName',
      'b2b.txt.ticketType',
      'b2b.table.quantity',
      'b2b.txt.unitPrice',
      'b2c.txt.totalPrice',
      'b2c.txt.pttt',
      'b2b.txt.orderer',
      'b2b.txt.ticket-status',
      'b2b.txt.contactType.representative',
      'b2b.txt.sapCode',
    ],
  });

  const columns = useMemo(
    () =>
      [
        {
          title: (
            <div style={{ textAlign: 'left', marginLeft: 20 }}>
              {translations?.['b2c.txt.date-order']}
            </div>
          ),
          dataIndex: 'orderDate',
          key: 'orderDate',
          render: orderDate => {
            if (_.isNil(orderDate))
              return (
                <div
                  style={{
                    minWidth: titleColumnSizes?.['b2c.txt.date-order'] ?? '',
                  }}
                />
              );

            const [day, time] = orderDate?.split(' ');

            return (
              <div
                style={{
                  textAlign: 'left',
                  marginLeft: 20,
                  minWidth: titleColumnSizes?.['b2c.txt.date-order'] ?? '',
                }}
              >
                <Typography>{day}</Typography>
                <Typography>{time}</Typography>
              </div>
            );
          },
        },
        {
          title: translations?.['b2c.txt.orderNumber'],
          key: 'orderId',
          dataIndex: 'orderId',
          render: order => {
            return (
              <div
                style={{
                  color: '#F8B117',
                  cursor: 'pointer',
                  minWidth: titleColumnSizes['b2c.txt.orderNumber'],
                }}
                onClick={() => {
                  if (order?.orderType === 'return')
                    history.push(`/ticket-refund/detail/${order?.id_hash}`, {
                      originPage: '/account?screen=detail',
                    });
                  else if (order?.orderType === 'upgrade')
                    history.push(
                      `/check-tickets/exchange-detail/${order?.id_hash}`,
                      {
                        screen: '/account',
                      },
                    );
                  else onClickShowDetail(order?.id_hash);
                }}
              >
                {order?.id}
              </div>
            );
          },
        },
        {
          title: translations?.['b2b.txt.root-order'],
          key: 'rootOrder',
          dataIndex: 'rootOrder',
          render: order => (
            <div
              style={{
                color: '#F8B117',
                cursor: isGetingDetail ? 'progress' : 'pointer',
                minWidth: titleColumnSizes['b2b.txt.root-order'],
              }}
              onClick={() => {
                if (isGetingDetail) return;
                onClickShowDetail(`${order?.orderIdChanged}`);
              }}
            >
              {order?.orderIdChanged ?? ''}
            </div>
          ),
        },
        {
          title: translations?.['b2b.txt.sapCode'],
          key: 'sapSubmitNumber',
          dataIndex: 'sapSubmitNumber',
          render: sapSubmitNumber => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.sapCode'],
              }}
            >
              {sapSubmitNumber}
            </div>
          ),
        },
        {
          title: translations?.['b2b.txt.ticketPark'],
          dataIndex: 'park',
          key: 'park',
          render: value => (
            <TooltipParagraph
              ellipsis={{ rows: 1 }}
              style={{
                marginBottom: 0,
                width: 200,
                textAlign: 'center',
              }}
            >
              {value}
            </TooltipParagraph>
          ),
        },
        {
          title: translations?.['b2b.txt.validFrom'],
          dataIndex: 'validFrom',
          key: 'validFrom',
          render: value => (
            <div style={{ minWidth: titleColumnSizes['b2b.txt.validFrom'] }}>
              {value}
            </div>
          ),
        },
        {
          title: translations?.['b2b.txt.validTo'],
          dataIndex: 'validTo',
          key: 'validTo',
          render: value => (
            <div style={{ minWidth: titleColumnSizes['b2b.txt.validTo'] }}>
              {value}
            </div>
          ),
        },
        {
          title: translations?.['b2b.txt.ticketName'],
          dataIndex: 'ticketName',
          key: 'ticketName',
          render: value => (
            <TooltipParagraph
              ellipsis={{ rows: 1 }}
              style={{
                marginBottom: 0,
                width: 200,
                textAlign: 'center',
              }}
            >
              {value}
            </TooltipParagraph>
          ),
        },
        {
          title: translations?.['b2b.txt.ticketType'],
          dataIndex: 'ticketType',
          key: 'ticketType',
          render: value => (
            <div style={{ minWidth: titleColumnSizes['b2b.txt.ticketType'] }}>
              {value}
            </div>
          ),
        },
        {
          title: translations?.['b2b.table.quantity'],
          dataIndex: 'quantity',
          key: 'quantity',
          render: value => (
            <div style={{ minWidth: titleColumnSizes['b2b.table.quantity'] }}>
              {value}
            </div>
          ),
        },
        {
          title: (
            <p style={{ textAlign: 'right', margin: 0 }}>
              {translations?.['b2b.txt.unitPrice']}
            </p>
          ),
          dataIndex: 'unitPrice',
          key: 'unitPrice',
          render: value => (
            <div
              style={{
                minWidth: titleColumnSizes['b2b.txt.unitPrice'],
                textAlign: 'right',
              }}
            >
              {_.isNil(value)
                ? null
                : `${formatNumber(+value).replaceAll(',', '.')}đ`}
            </div>
          ),
        },
        {
          title: (
            <p style={{ textAlign: 'right', margin: 0 }}>
              {translations?.['b2c.txt.totalPrice']}
            </p>
          ),
          dataIndex: 'totalPrice',
          key: 'totalPrice',
          render: value => (
            <div
              style={{
                minWidth: titleColumnSizes['b2c.txt.totalPrice'],
                textAlign: 'right',
              }}
            >
              {_.isNil(value)
                ? null
                : `${formatNumber(+value).replaceAll(',', '.')}đ`}
            </div>
          ),
        },
        {
          title: translations?.['b2c.txt.pttt'],
          dataIndex: 'payment',
          key: 'payment',
          render: payment => (
            <div
              style={{
                minWidth: Math.max(titleColumnSizes['b2c.txt.pttt'], 150),
              }}
            >
              {payment}
            </div>
          ),
        },
        {
          title: (
            <div style={{ textAlign: 'center' }}>
              {translations?.['b2b.txt.orderer']}
            </div>
          ),
          key: 'orderer',
          dataIndex: 'orderer',
          render: orderer => (
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <TooltipParagraph
                ellipsis={{ rows: 1 }}
                style={{
                  marginBottom: 0,
                  width: 164,
                  textAlign: 'center',
                }}
              >
                {orderer}
              </TooltipParagraph>
            </div>
          ),
        },
        {
          title: translations?.['b2b.txt.contactType.representative'],
          dataIndex: 'representativesInfo',
          key: 'representativesInfo',
          render: (representativesInfo, record) => {
            if (
              !_.isArray(representativesInfo) ||
              !representativesInfo?.length ||
              !representativesInfo?.[0]?.name
            ) {
              return (
                <div
                  style={{
                    minWidth:
                      titleColumnSizes['b2b.txt.contactType.representative'],
                  }}
                />
              );
            }

            return (
              <StyledPopover
                overlayClassName="representative-popover"
                overlayInnerStyle={{ padding: 0 }}
                content={
                  representativesInfo.length > 0 ? (
                    <RepresentativeList
                      representativesInfo={representativesInfo}
                    />
                  ) : null
                }
              >
                <div
                  style={{
                    minWidth:
                      titleColumnSizes['b2b.txt.contactType.representative'],
                  }}
                >{`${representativesInfo?.[0]?.name}${
                  representativesInfo.length >= 2
                    ? `,+${representativesInfo.length - 1}`
                    : ''
                }`}</div>
              </StyledPopover>
            );
          },
        },
        {
          title: translations?.['b2b.txt.ticket-status'],
          key: 'orderState',
          dataIndex: 'orderState',
          render: orderState => {
            return (
              <div
                style={{ minWidth: titleColumnSizes['b2b.txt.ticket-status'] }}
              >
                {orderState}
              </div>
            );
          },
        },
      ].filter(item => typeof item !== 'boolean'),
    [
      history,
      isGetingDetail,
      onClickShowDetail,
      titleColumnSizes,
      translations,
    ],
  );

  const listItemData = useMemo(
    () => listItemResponse?.data ?? [],
    [listItemResponse],
  );

  const dataSource = useMemo(() => {
    return listItemData.map(item => ({
      orderDate: item?.orderDate,
      orderId: item,
      rootOrder: item,
      park: item?.parkName,
      validFrom: item?.usageFrom,
      validTo: item?.usageTo,
      ticketName: item?.ticketShowName,
      ticketType: item?.ticketTypeName,
      quantity: item?.quantity,
      unitPrice: item?.originalPrice,
      totalPrice: item?.totalPrice,
      payment: item?.paymentName,
      orderer: item?.agentName,
      representativesInfo: item?.representativesInfo,
      orderState: item?.statusTicket,
      sapSubmitNumber: item?.sapSubmitNumber,
    }));
  }, [listItemData]);

  const disabledExportExcel = dataSource?.length < 1;

  const handleSearch = (data: FilterFormValues) => {
    const newFilter: FilterFormValues = { ...data, page: 1 };
    filterRef.current = newFilter;
    filterForm.reset(newFilter);

    onFetchListItem({
      payloadData: transfromFilterValues(newFilter),
      callback: {
        onSuccess: () => {
          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  const handleExportExcel = async () => {
    try {
      setPageLoading(true);
      const { page, limit, ...exportExcelPayload } = transfromFilterValues(
        filterForm.getValues(),
      );
      const res = await exportHistoryExcelItemListApi(exportExcelPayload);

      if (res?.data?.success) {
        window.location.href = res?.data?.data?.path;
      }
    } catch (error: any) {
      onOpenDialogNoti({
        type: 'ERROR',
        content: error?.message ?? '',
        isShow: true,
        title: '',
        onCloseDialog: onCloseDialogNoti,
      });
    } finally {
      setPageLoading(false);
    }
  };

  const handleChangePage = async (page: number, pageSize: number) => {
    filterForm.setValue('page', page);
    filterForm.setValue('limit', pageSize);

    filterRef.current = {
      ...filterRef.current,
      page,
      limit: pageSize,
    };

    onFetchListItem({
      payloadData: transfromFilterValues(filterRef.current),
      callback: {
        onSuccess: () => {
          window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });

          tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      },
    });
  };

  useEffect(() => {
    if (initFetchListItem.current) {
      onFetchListItem();
      initFetchListItem.current = false;
    }
  }, [onFetchListItem]);

  return (
    <LoadingContainer loading={pageLoading}>
      <div
        style={{
          display: 'flex',
          flexDirection: isDesktop ? 'column' : 'row',
          rowGap: 20,
          columnGap: 8,
          marginBottom: 20,
          justifyContent: isDesktop ? 'flex-start' : 'flex-end',
        }}
      >
        {isDesktop && (
          <FormProvider {...filterForm}>
            <div ref={anchorPointerRef}>
              <Filter onSearch={handleSearch} />
            </div>
          </FormProvider>
        )}

        <div style={{ width: '100%', marginTop: isDesktop ? 8 : 0 }}>
          <StyledTabs
            activeKey="success"
            tabBarExtraContent={{
              right: (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {!isDesktop && (
                    <FormProvider {...filterForm}>
                      <div ref={anchorPointerRef}>
                        <Filter onSearch={handleSearch} />
                      </div>
                    </FormProvider>
                  )}

                  <Button
                    onClick={handleExportExcel}
                    disabled={disabledExportExcel}
                    style={{
                      filter: `grayscale(${disabledExportExcel ? '1' : '0'})`,
                      cursor: disabledExportExcel ? 'not-allowed' : 'pointer',
                      alignSelf: 'flex-end',
                      padding: 0,
                    }}
                  >
                    <div
                      style={
                        isDesktop
                          ? {}
                          : {
                              border: '1px solid #ededed',
                              width: 41,
                              aspectRatio: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 10,
                            }
                      }
                    >
                      <ExportExcel
                        style={isDesktop ? {} : { width: 16, height: 16 }}
                      />
                    </div>
                    {isDesktop && (
                      <SmallTitle
                        style={{
                          margin: 0,
                          color: '#fab317',
                          fontSize: 14,
                          width: 'max-content',
                          marginLeft: 6,
                          fontWeight: 400,
                        }}
                      >
                        {translations?.['b2b.txt.export-excel']}
                      </SmallTitle>
                    )}
                  </Button>
                </div>
              ),
            }}
          >
            <StyledTabs.TabPane
              key="success"
              tab={
                (translations ? translations['b2c.txt.success'] : '') +
                ' (' +
                (listItemResponse?.paginator?.totalCount ?? 0) +
                ')'
              }
            >
              <div>
                <StyledTable
                  tableRef={tableRef}
                  columns={columns}
                  dataSource={dataSource}
                  emptyText={translations?.['b2b.txt.noOrderFound'] ?? ''}
                  tableProps={{
                    pagination: false,
                    scroll: {
                      x: dataSource?.length ? 'auto' : 'max-content',
                      y: dataSource?.length ? '50vh' : undefined,
                    },
                  }}
                  styleSlots={{
                    antTableContainer: {
                      overflow: 'hidden',
                    },
                    antTableHeader: {
                      border: 'none !important',
                      borderRadius: '0 !important',
                    },
                    tableCSS: {
                      borderTopLeftRadius: !dataSource?.length
                        ? ''
                        : '0 !important',
                      borderTopRightRadius: !dataSource?.length
                        ? ''
                        : '0 !important',
                    },
                    antTableBody: {
                      paddingBottom: 10,
                    },
                  }}
                />
                <StyledPagination
                  showSizeChanger
                  style={{ margin: 'auto', marginTop: 24 }}
                  current={filterForm.getValues('page') || 1}
                  total={listItemResponse?.paginator?.totalCount || 0}
                  pageSize={filterForm.getValues('limit') || 20}
                  onChange={handleChangePage}
                  pageSizeOptions={['20', '50', '100']}
                />
              </div>
            </StyledTabs.TabPane>
          </StyledTabs>
        </div>
      </div>
    </LoadingContainer>
  );
};

export default PurcharseHistoryDetail;

const RangeDatePickerContainer = styled(Col)`
  .range-picker-container {
    width: 100%;
  }
  .react-datepicker__close-icon {
    right: 5px;
  }
  .date-picker {
    height: 44px;
  }
`;

const Button = styled.button`
  display: flex;
  align-items: flex-end;
  border: none;
  outline: none;
  background-color: transparent;
`;

const SmallTitle = styled.h6`
  font-weight: 500;
  font-size: 12px;
  color: #57575c;
  margin: 0;
  margin-bottom: 8px;
`;
